import { ApiResponse } from "types/app";
import API from "Classes/Api";
import { timeZoneOffset } from "utils";
import { AssessHostsForCloudResultType, AssessmentHostsForCloud } from "types/assessmentTypes";
import { FindingsUpdateParams } from "types/findingTypes";
import { DataCollector } from "types/onboardingTypes";
import { ApiGetCallParams, ApiPOSTBlobCallParams, ApiPostCallParams } from "Classes/ApiCallParams";
import { DatabasesSummaryApiResponse } from "pages/Dashboards/Databases/DatabaseTypes/DatabaseSummaryTypes";
import { GetCollectorEnvironmentsResponse } from "../ServicesTypes/Environment";
import { DatabaseAllocationHistogramApiResponse } from "./EnvironmentTypes";

class Environment extends API {
  async getHostsSummary({ clientId, startDateTime, endDateTime, filters = {}, size = 1000 }) {
    const result = await super.call(
      new ApiPostCallParams({
        action: `/v2/clients/${clientId}/dashboards/environment/hosts/overview`,
        headers: { ...API.getAuthHeaders() },
        body: {
          startDateTime,
          endDateTime,
          timeZoneOffset,
          filters,
          size,
        },
        failureMessage: "Failed to get hosts summary",
      })
    );
    return result;
  }

  async getHostDetails({ clientId, startDateTime, endDateTime, hostName }) {
    const result = await super.call(
      new ApiPostCallParams({
        action: `/v2/clients/${clientId}/dashboards/environment/hosts/detail`,
        headers: { ...API.getAuthHeaders() },
        body: {
          startDateTime,
          endDateTime,
          timeZoneOffset,
          hostName,
        },
        failureMessage: "Failed to get host details",
      })
    );
    return result;
  }

  async getInstancesSummary({ clientId, startDateTime, endDateTime, filters = {} }) {
    const result = await super.call(
      new ApiPostCallParams({
        action: `/v2/clients/${clientId}/dashboards/environment/instances/overview`,
        headers: { ...API.getAuthHeaders() },
        body: {
          startDateTime,
          endDateTime,
          timeZoneOffset,
          filters,
        },
        failureMessage: "Failed to get instances summary",
      })
    );
    return result;
  }

  async getInstancesDetails({ clientId, startDateTime, endDateTime, instanceName }) {
    const result = await super.call(
      new ApiPostCallParams({
        action: `/v2/clients/${clientId}/dashboards/environment/instances/detail`,
        headers: { ...API.getAuthHeaders() },
        body: {
          startDateTime,
          endDateTime,
          timeZoneOffset,
          instanceName,
        },
        failureMessage: "Failed to get instances details",
      })
    );
    return result;
  }

  async getDatabasesSummary({
    clientId,
    startDateTime,
    endDateTime,
    filters = {},
  }): Promise<DatabasesSummaryApiResponse> {
    const result = await super.call(
      new ApiPostCallParams({
        action: `/v2/clients/${clientId}/dashboards/environment/databases/overview`,
        headers: { ...API.getAuthHeaders() },
        body: {
          startDateTime,
          endDateTime,
          timeZoneOffset,
          filters,
        },
        failureMessage: "Failed to get databases summary",
      })
    );
    return result;
  }

  async getDatabasesDetails({ clientId, startDateTime, endDateTime, instanceName, databaseName }) {
    const result = await super.call(
      new ApiPostCallParams({
        action: `/v2/clients/${clientId}/dashboards/environment/databases/detail`,
        headers: { ...API.getAuthHeaders() },
        body: {
          startDateTime,
          endDateTime,
          timeZoneOffset,
          instanceName,
          databaseName,
        },
        failureMessage: "Failed to get databases details",
      })
    );
    return result;
  }

  async getCollectorEnvironments(
    clientId: string | number
  ): Promise<GetCollectorEnvironmentsResponse> {
    const query = {};
    const result = await super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/collector-environments`,
        headers: { ...API.getAuthHeaders() },
        query,
      })
    );

    return result;
  }

  //non cached full properties version
  async getMonitoringTypes(clientId) {
    const query = {};
    const result = await super.call(
      new ApiGetCallParams({
        action: `clients/${clientId}/environment/monitoring_types`,
        headers: { ...API.getAuthHeaders() },
        query,
      })
    );

    return result;
  }

  async addOrUpdateDataCollector(clientId: number, dataCollector: DataCollector) {
    try {
      const result = await super.call(
        new ApiPostCallParams({
          action: `clients/${clientId}/collector_environment`,
          headers: { ...API.getAuthHeaders() },
          body: {
            data: {
              ...dataCollector,
              servers: dataCollector.Servers.length,
              serverList: dataCollector.ServersAsString,
            },
          }, // server class have a servers numeric  field
        })
      );

      return result;
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  async getHostsAssessment(
    clientId: number,
    params: AssessmentHostsForCloud,
    resultType: AssessHostsForCloudResultType
  ) {
    if (resultType == AssessHostsForCloudResultType.Data) {
      const result = await super.call(
        new ApiPostCallParams({
          action: `clients/${clientId}/environment/assess_hosts_for_cloud`,
          headers: { ...API.getAuthHeaders() },
          body: {
            data: {
              ...params,
              useLocalDriveForTempDB: params.useLocalDriveForTempDB == "Yes",
              percentile: Number(params.percentile),
              resultType,
            },
          },
        })
      );

      return result;
    } else {
      await super.call(
        new ApiPOSTBlobCallParams({
          action: `clients/${clientId}/environment/assess_hosts_for_cloud`,
          headers: { ...API.getAuthHeaders() },
          body: {
            data: {
              ...params,
              useLocalDriveForTempDB: params.useLocalDriveForTempDB == "Yes",
              percentile: Number(params.percentile),
            },
          },
        })
      );
    }
  }

  async updateFinding(clientId: number, params: FindingsUpdateParams): Promise<ApiResponse<null>> {
    const result = await super.call(
      new ApiPostCallParams({
        action: `dashboards/${clientId}/finding`,
        headers: { ...API.getAuthHeaders() },
        body: {
          data: params,
        },
      })
    );
    return result;
  }

  async getInstanceAllocationHistogram({ clientId, startDateTime, endDateTime, filters = {} }) {
    const result = await super.call(
      new ApiPostCallParams({
        action: `v2/clients/${clientId}/dashboards/environment/instances/allocation-histogram`,
        headers: { ...API.getAuthHeaders() },
        body: {
          startDateTime,
          endDateTime,
          timeZoneOffset,
          filters,
        },
        failureMessage: "Failed to get instances allocation histogram",
      })
    );
    return result;
  }
  async getDatabaseAllocationHistogram({
    clientId,
    startDateTime,
    endDateTime,
    filters = {},
  }): Promise<DatabaseAllocationHistogramApiResponse> {
    const result = await super.call(
      new ApiPostCallParams({
        action: `v2/clients/${clientId}/dashboards/environment/databases/allocation-histogram`,
        headers: { ...API.getAuthHeaders() },
        body: {
          startDateTime,
          endDateTime,
          timeZoneOffset,
          filters,
        },
        failureMessage: "Failed to get databases allocation histogram",
      })
    );
    return result;
  }
}

const instance = new Environment();
export default instance;
